import {
  enviarMensaje as apiEnviarMensaje,
  listarMensajes as apiListarMensajes,
  marcarLeidos as apiMarcarLeidos,
} from 'api/solicitudes/chat'
import { useCallback, useEffect, useRef, useState } from 'react'

const generarTmpId = () => `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const useChatSolicitud = ({ codigoSolicitud, origenSolicitud }) => {
  const [mensajes, setMensajes] = useState([])
  const [refrescando, setRefrescando] = useState(false)
  const [error, setError] = useState(null)
  // Cache de payloads por tmpId para poder reintentar con el mismo contenido
  const payloadsRef = useRef(new Map())

  const cargar = useCallback(() => {
    if (!codigoSolicitud || !origenSolicitud) return
    setRefrescando(true)
    apiListarMensajes(codigoSolicitud, origenSolicitud)
      .then((data) => setMensajes(data || []))
      .catch((e) => setError(e))
      .finally(() => setRefrescando(false))
  }, [codigoSolicitud, origenSolicitud])

  useEffect(() => {
    cargar()
  }, [cargar])

  const intentarEnvio = useCallback(
    async (tmpId, mensaje, fichero) => {
      try {
        const respuesta = await apiEnviarMensaje(codigoSolicitud, origenSolicitud, mensaje, fichero)
        setMensajes((prev) =>
          prev.map((m) =>
            m.id === tmpId ? { ...m, ...respuesta, enviando: false, error: false } : m,
          ),
        )
        payloadsRef.current.delete(tmpId)
      } catch (e) {
        setMensajes((prev) =>
          prev.map((m) => (m.id === tmpId ? { ...m, enviando: false, error: true } : m)),
        )
        setError(e)
      }
    },
    [codigoSolicitud, origenSolicitud],
  )

  const enviar = useCallback(
    async (mensaje, fichero = null, usuarioActual = null) => {
      const tmpId = generarTmpId()
      // Enriquecemos la burbuja optimista con el autor real y, si hay
      // fichero adjunto, con su nombre + URL temporal para previsualizar
      // la descarga antes de que el servidor responda (AC-CHATSOL-4).
      const usuarioWeb = usuarioActual?.usuario_web
      const nombreAutor = usuarioActual?.nombre || usuarioActual?.nombre_autor
      let urlFichero = null
      let nombreFichero = null
      if (fichero) {
        nombreFichero = fichero.name
        try {
          if (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
            urlFichero = URL.createObjectURL(fichero)
          }
        } catch {
          urlFichero = null
        }
      }
      const mensajeOptimista = {
        id: tmpId,
        mensaje,
        fecha: new Date().toISOString(),
        enviando: true,
        error: false,
        ...(usuarioWeb ? { usuario_web: usuarioWeb } : {}),
        ...(nombreAutor ? { nombre_autor: nombreAutor } : {}),
        ...(urlFichero ? { url_fichero: urlFichero } : {}),
        ...(nombreFichero ? { nombre_fichero: nombreFichero } : {}),
      }
      setMensajes((prev) => [...prev, mensajeOptimista])
      payloadsRef.current.set(tmpId, { mensaje, fichero, usuarioActual })
      await intentarEnvio(tmpId, mensaje, fichero)
    },
    [intentarEnvio],
  )

  const reintentar = useCallback(
    async (tmpId) => {
      const payload = payloadsRef.current.get(tmpId)
      if (!payload) return
      setMensajes((prev) =>
        prev.map((m) => (m.id === tmpId ? { ...m, enviando: true, error: false } : m)),
      )
      await intentarEnvio(tmpId, payload.mensaje, payload.fichero)
    },
    [intentarEnvio],
  )

  const marcarLeidos = useCallback(
    async (chatIds) => {
      if (!chatIds || chatIds.length === 0) return
      try {
        await apiMarcarLeidos(codigoSolicitud, origenSolicitud, chatIds)
        setMensajes((prev) => prev.map((m) => (chatIds.includes(m.id) ? { ...m, leido: true } : m)))
      } catch (e) {
        setError(e)
      }
    },
    [codigoSolicitud, origenSolicitud],
  )

  return { mensajes, enviar, reintentar, marcarLeidos, refrescando, error }
}

export default useChatSolicitud
